/**
 * MobileTasksListPage - PIXEL PERFECT selon maquette
 */

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Menu, Plus, Zap, MoreVertical, Calendar, Check,
  AlertCircle, ChevronRight, ArrowLeft, X, Trash2,
  Sparkles, Mic, ArrowUp, Paperclip,
  MessageCircle, Bell, User
} from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';

// DONNÉES DÉMO
const DEMO_TASKS = [
  { id: '1', title: 'Relancer annonce table basse', description: 'Pas de réponse depuis 7 jours', priority: 'low', status: 'todo', due_date: 'Hier', tags: ['annonce', 'visibilité'] },
  { id: '2', title: 'Prendre photos vélo', description: 'Ajouter des photos pour augmenter la visibilité', priority: 'medium', status: 'todo', due_date: "Aujourd'hui", tags: ['annonce', 'photos'] },
  { id: '3', title: 'Comparer devis électricien', description: '3 devis reçus pour installation tableau', priority: 'medium', status: 'todo', due_date: '24 mai', tags: ['artisan', 'devis'] },
  { id: '4', title: 'Confirmer réception chaise', description: "Colis livré hier – confirmer l'état", priority: 'medium', status: 'todo', due_date: '24 mai', tags: ['achat'] },
  { id: '5', title: 'Répondre à Pierre M.', description: 'Message reçu il y a 2h concernant le canapé', priority: 'high', status: 'todo', due_date: '24 mai', tags: ['urgent', 'message'] }
];

// FILTRE PREMIUM - Chip scrollable COMPACT (sans icône)
const FilterChip = ({ label, isActive, onClick, isRed, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 h-[36px] rounded-full text-[12px] font-semibold border whitespace-nowrap transition-all flex-shrink-0 ${
      isActive 
        ? 'bg-[#F97316] text-white border-[#F97316] shadow-md shadow-orange-200' 
        : isRed
          ? 'bg-white border-red-200 text-red-500 shadow-sm'
          : 'bg-white border-[#E8E2DC] text-[#1E3A5F] shadow-sm'
    }`}
  >
    <span>{label}</span>
    {count !== undefined && count > 0 && (
      <span className={`text-[10px] px-1 py-0.5 rounded-full ${isActive ? 'bg-white/20 text-white' : isRed ? 'bg-red-100 text-red-500' : 'bg-gray-100 text-gray-600'}`}>
        {count}
      </span>
    )}
  </button>
);

// CARTE TÂCHE - COMPACTE ET CLIQUABLE
const TaskCard = ({ task, onToggle, onOpen }) => {
  const priorityStyles = {
    high: { label: 'Urgent', style: 'text-red-500 bg-red-50 border-red-200', icon: AlertCircle },
    medium: { label: 'Normal', style: 'text-orange-500 bg-white border-orange-300', icon: null },
    low: { label: 'Faible', style: 'text-gray-500 bg-gray-50 border-gray-200', icon: Check }
  };
  const p = priorityStyles[task.priority] || priorityStyles.medium;

  return (
    <div 
      onClick={() => onOpen(task)}
      className="bg-white rounded-xl border-l-4 border-l-[#F97316] mx-4 mb-2 shadow-sm active:bg-gray-50 cursor-pointer"
    >
      <div className="px-3 py-2.5">
        <div className="flex items-start gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); onToggle(task); }}
            className={`w-[18px] h-[18px] mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
              task.status === 'done' ? 'bg-green-500 border-green-500' : 'border-gray-300'
            }`}
          >
            {task.status === 'done' && <Check size={10} className="text-white" />}
          </button>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-[13px] text-gray-900 leading-tight">{task.title}</h3>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <span className="text-[11px] text-[#F97316] flex items-center gap-0.5 font-medium">
                  <Calendar size={11} />
                  {task.due_date}
                </span>
                {task.files?.length > 0 && (
                  <Paperclip size={12} className="text-gray-400 ml-1" />
                )}
                <ChevronRight size={16} className="text-gray-400" />
              </div>
            </div>
            
            <p className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">{task.description}</p>
            
            {/* Tags compacts */}
            <div className="flex items-center gap-1 mt-1.5 overflow-hidden">
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium border flex-shrink-0 ${p.style}`}>
                {p.icon && <p.icon size={8} />}
                {p.label}
              </span>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium text-red-500 bg-red-50 border border-red-200 flex-shrink-0">
                <Calendar size={8} />
                En retard
              </span>
              {task.tags?.slice(0, 2).map((tag, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-full text-[9px] bg-gray-100 text-gray-600 flex-shrink-0">#{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// COMPOSANT PRINCIPAL
export default function MobileTasksListPage({ 
  onOpenMenu, 
  onOpenMessages,
  onOpenNotifications, 
  onOpenProfile,
  onOpenCoins,
  onBackToHub,
  onNavigateToHub,
  isPro = false 
}) {
  const [tasks, setTasks] = useState(DEMO_TASKS);
  const [filter, setFilter] = useState('all');
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantTaskContext, setAssistantTaskContext] = useState(null);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  // Champs nouvelle tâche
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskDueTime, setNewTaskDueTime] = useState('');
  const [newTaskFiles, setNewTaskFiles] = useState([]);
  // Champs édition tâche
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('medium');
  const [editDueDate, setEditDueDate] = useState('');
  const [editDueTime, setEditDueTime] = useState('');
  const [editFiles, setEditFiles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  
  // État pour détecter le clavier mobile
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  
  const fileInputRef = React.useRef(null);
  const editFileInputRef = React.useRef(null);
  
  // Détection du clavier via visualViewport API
  React.useEffect(() => {
    if (!showNewTaskModal) {
      setKeyboardHeight(0);
      return;
    }
    
    // Masquer la navbar quand le modal est ouvert
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleResize = () => {
      // La différence entre la hauteur de la fenêtre et le viewport visible = hauteur du clavier
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, [showNewTaskModal]);
  
  const done = tasks.filter(t => t.status === 'done').length;
  const overdue = tasks.filter(t => t.status !== 'done').length;
  const inProgressCount = tasks.filter(t => t.status === 'inProgress').length;
  const waitingCount = tasks.filter(t => t.status === 'waiting').length;
  const todoCount = tasks.filter(t => t.status === 'todo').length;

  const toggle = (task) => {
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: t.status === 'done' ? 'todo' : 'done' } : t));
    toast.success(task.status === 'done' ? 'Tâche réouverte' : 'Tâche terminée !');
  };

  const openTaskDetail = (task) => {
    setSelectedTask(task);
    setEditTitle(task.title);
    setEditDescription(task.description || '');
    setEditPriority(task.priority || 'medium');
    setEditDueDate(task.due_date_raw || '');
    setEditDueTime(task.due_time || '');
    setEditFiles(task.files || []);
  };

  const closeTaskDetail = () => {
    setSelectedTask(null);
  };

  const handleUpdateTask = () => {
    if (!editTitle.trim()) return;
    setTasks(prev => prev.map(t => 
      t.id === selectedTask.id 
        ? { 
            ...t, 
            title: editTitle,
            description: editDescription,
            priority: editPriority,
            due_date: editDueDate 
              ? new Date(editDueDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
              : t.due_date,
            due_date_raw: editDueDate,
            due_time: editDueTime,
            files: editFiles
          }
        : t
    ));
    toast.success('Tâche mise à jour !');
    closeTaskDetail();
  };

  const handleDeleteTask = () => {
    setTasks(prev => prev.filter(t => t.id !== selectedTask.id));
    toast.success('Tâche supprimée');
    closeTaskDetail();
  };

  const handleFileUpload = (e, isEdit = false) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: (file.size / 1024).toFixed(1) + ' Ko',
      type: file.type
    }));
    if (isEdit) {
      setEditFiles(prev => [...prev, ...newFiles]);
    } else {
      setNewTaskFiles(prev => [...prev, ...newFiles]);
    }
    toast.success(`${files.length} fichier(s) ajouté(s)`);
  };

  const removeFile = (fileId, isEdit = false) => {
    if (isEdit) {
      setEditFiles(prev => prev.filter(f => f.id !== fileId));
    } else {
      setNewTaskFiles(prev => prev.filter(f => f.id !== fileId));
    }
  };

  const openAbraForTask = (task) => {
    setAssistantTaskContext(task);
    setSelectedTask(null); // Ferme le détail de la tâche
    // Petit délai pour laisser l'animation de fermeture se terminer
    setTimeout(() => {
      setShowAssistant(true);
    }, 100);
  };

  const resetNewTaskForm = () => {
    setNewTaskTitle('');
    setNewTaskDescription('');
    setNewTaskPriority('medium');
    setNewTaskDueDate('');
    setNewTaskDueTime('');
    setNewTaskFiles([]);
  };

  const handleAddTask = () => {
    if (!newTaskTitle.trim()) return;
    const newTask = {
      id: Date.now(),
      title: newTaskTitle,
      description: newTaskDescription || 'Aucune description',
      status: 'todo',
      priority: newTaskPriority,
      due_date: newTaskDueDate 
        ? new Date(newTaskDueDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
        : new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
      due_date_raw: newTaskDueDate,
      due_time: newTaskDueTime,
      files: newTaskFiles,
      tags: ['nouveau']
    };
    setTasks(prev => [newTask, ...prev]);
    resetNewTaskForm();
    setShowNewTaskModal(false);
    toast.success('Tâche créée !');
  };

  const handleMessages = () => {
    if (onOpenMessages) onOpenMessages();
    else toast.info('Messages - Bientôt disponible');
  };

  const handleNotifications = () => {
    if (onOpenNotifications) onOpenNotifications();
    else toast.info('Notifications - Bientôt disponible');
  };

  const handleProfile = () => {
    if (onOpenProfile) onOpenProfile();
    else toast.info('Profil - Bientôt disponible');
  };

  const handleCoins = () => {
    if (onOpenCoins) onOpenCoins();
    else toast.info('4.3k AbracadaCoins disponibles');
  };

  const send = () => {
    if (!inputValue.trim() || isLoading) return;
    setMessages(prev => [...prev, { role: 'user', content: inputValue }]);
    setInputValue('');
    setIsLoading(true);
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', content: `Je t'aide avec "${inputValue}".` }]);
      setIsLoading(false);
    }, 1000);
  };

  const filtered = tasks.filter(t => {
    if (filter === 'todo') return t.status === 'todo';
    if (filter === 'done') return t.status === 'done';
    if (filter === 'overdue') return t.status !== 'done';
    if (filter === 'inProgress') return t.status === 'inProgress';
    if (filter === 'waiting') return t.status === 'waiting';
    return true;
  });

  return (
    <div 
      className={`fixed left-0 right-0 z-50 flex flex-col ${isDarkMode ? 'bg-[#0f172a]' : 'bg-gray-50'}`}
      style={{ top: '94px', bottom: '60px' }}
    >
        
        {/* Titre + Boutons - Header épuré */}
        <div className={`flex items-center justify-between px-3 py-2.5 border-b ${isDarkMode ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'}`}>
          <div className="flex items-center gap-1">
            {/* Flèche retour vers le Hub Mon Espace */}
            {onBackToHub && (
              <button 
                onClick={onBackToHub}
                className="p-1"
                data-testid="back-to-hub-btn"
              >
                <ArrowLeft size={20} className={isDarkMode ? 'text-white' : 'text-[#1E3A5F]'} strokeWidth={2} />
              </button>
            )}
            <div>
              <h1 className={`text-[16px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Mes tâches</h1>
              <p className={`text-[10px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {done}/{tasks.length} terminées
              </p>
              <p className="text-[10px] text-red-500 font-semibold">
                {overdue} en retard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Bouton Éclair - Ouvre modale automatisation */}
            <button 
              onClick={() => setShowAutoModal(true)}
              className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-orange-900/30 border-orange-700' : 'bg-orange-50 border-orange-200'} border`}
            >
              <Zap size={14} className="text-[#F97316]" fill="#F97316" />
            </button>
            <button 
              onClick={() => setShowNewTaskModal(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[11px] whitespace-nowrap"
            >
              <Plus size={12} />
              Nouvelle tâche
            </button>
          </div>
        </div>
        
        {/* Barre progression - Compacte */}
        <div className={`mx-4 mb-1.5 h-1 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div className="h-full bg-[#1E3A5F] rounded-full" style={{ width: `${(done / tasks.length) * 100}%` }} />
        </div>

      {/* CONTENU SCROLLABLE */}
      <div className="flex-1 overflow-y-auto pb-24">

        {/* FILTRES - Barre horizontale scrollable */}
        <div className="relative py-1.5">
          <div 
            className="flex gap-1.5 overflow-x-auto px-4 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
          >
            <FilterChip 
              label="Toutes" 
              isActive={filter === 'all'} 
              onClick={() => setFilter('all')}
              count={tasks.length}
            />
            <FilterChip 
              label="En retard" 
              isActive={filter === 'overdue'} 
              onClick={() => setFilter('overdue')}
              isRed={filter !== 'overdue'}
              count={overdue}
            />
            <FilterChip 
              label="À faire" 
              isActive={filter === 'todo'} 
              onClick={() => setFilter('todo')}
            />
            <FilterChip 
              label="En cours" 
              isActive={filter === 'inProgress'} 
              onClick={() => setFilter('inProgress')}
            />
            <FilterChip 
              label="En attente" 
              isActive={filter === 'waiting'} 
              onClick={() => setFilter('waiting')}
            />
            <FilterChip 
              label="Terminées" 
              isActive={filter === 'done'} 
              onClick={() => setFilter('done')}
              count={done}
            />
            {/* Spacer pour le padding droit */}
            <div className="w-6 flex-shrink-0" />
          </div>
          
          {/* Dégradé + Flèche à droite pour indiquer le scroll */}
          <div 
            className="absolute right-0 top-0 bottom-0 w-10 pointer-events-none flex items-center justify-end pr-1"
            style={{
              background: `linear-gradient(to right, transparent, ${isDarkMode ? '#0f172a' : '#f5f5f5'} 70%)`
            }}
          >
            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-700' : 'bg-white'} shadow-sm`}>
              <ChevronRight size={12} className={isDarkMode ? 'text-gray-400' : 'text-[#1E3A5F]'} />
            </div>
          </div>
        </div>

        {/* Tâches */}
        <div>
          {filtered.map(task => (
            <TaskCard key={task.id} task={task} onToggle={toggle} onOpen={openTaskDetail} />
          ))}
        </div>
      </div>

      {/* ═══ BOUTON FLOTTANT "DEMANDER À ABRA" - Style Corail/Terracotta Premium ═══ */}
      <button
        onClick={() => { setAssistantTaskContext(null); setShowAssistant(true); }}
        className="fixed z-50 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white active:scale-95 transition-all"
        style={{ 
          bottom: 'calc(56px + env(safe-area-inset-bottom, 0px) + 16px)',
          right: '16px',
          background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)',
          boxShadow: '0 4px 14px rgba(232, 149, 92, 0.30)',
          border: '1px solid rgba(255, 255, 255, 0.12)'
        }}
      >
        <Sparkles size={14} className="text-white" />
        <span className="text-[12px] font-semibold">Demander à Abra</span>
      </button>

      {/* La navbar est gérée par CockpitAssistantUnified avec z-index 9999 */}

      {/* ═══ MODALE AUTOMATISATION ═══ */}
      <AnimatePresence>
        {showAutoModal && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-50"
              onClick={() => setShowAutoModal(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl p-5 pb-8"
            >
              <div className="w-10 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Zap size={20} className="text-[#F97316]" fill="#F97316" />
                </div>
                <div>
                  <h3 className="font-bold text-[16px] text-gray-900">Tâches automatiques</h3>
                  <p className="text-[12px] text-gray-500">Abra t'aide à ne rien oublier</p>
                </div>
              </div>
              <p className="text-[13px] text-gray-600 mb-4">
                Abra peut créer automatiquement des tâches à partir de tes messages, ventes, annonces, emails ou rendez-vous.
              </p>
              <div className="space-y-2">
                <button 
                  onClick={() => { setShowAutoModal(false); setAssistantTaskContext(null); setShowAssistant(true); }}
                  className="w-full py-3 bg-[#F97316] text-white rounded-xl font-semibold text-[14px]"
                >
                  Gérer avec Abra
                </button>
                <button 
                  onClick={() => setShowAutoModal(false)}
                  className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium text-[14px]"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══ NOUVELLE TÂCHE - PLEIN ÉCRAN MOBILE via Portal ═══ */}
      {showNewTaskModal && createPortal(
        <AnimatePresence>
          <motion.div
            key="new-task-modal"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed inset-0 z-[10002] flex flex-col ${isDarkMode ? 'bg-[#0f172a]' : 'bg-white'}`}
          >
            {/* Header - Simple avec flèche retour + titre + safe area */}
            <header 
              className={`flex items-center gap-2 px-3 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button 
                onClick={() => { resetNewTaskForm(); setShowNewTaskModal(false); }}
                className={`p-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <ArrowLeft size={22} />
              </button>
              <h1 className={`text-[15px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Nouvelle tâche</h1>
            </header>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Titre */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Titre *
                </label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                  placeholder="Qu'est-ce que tu dois faire ?"
                  className={`w-full p-4 rounded-xl border text-[16px] ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
              
              {/* Description */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Description
                </label>
                <textarea
                  value={newTaskDescription}
                  onChange={(e) => setNewTaskDescription(e.target.value)}
                  placeholder="Ajoute des détails..."
                  rows={4}
                  className={`w-full p-4 rounded-xl border text-[15px] resize-none ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white placeholder-gray-500' 
                      : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
              
              {/* Date et Heure d'échéance */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Échéance
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="date"
                      value={newTaskDueDate}
                      onChange={(e) => setNewTaskDueDate(e.target.value)}
                      className={`w-full p-4 rounded-xl border text-[15px] ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="w-28">
                    <input
                      type="time"
                      value={newTaskDueTime}
                      onChange={(e) => setNewTaskDueTime(e.target.value)}
                      className={`w-full p-4 rounded-xl border text-[15px] ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      }`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Priorité */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Priorité
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'low', label: 'Faible', color: 'gray' },
                    { value: 'medium', label: 'Normale', color: 'orange' },
                    { value: 'high', label: 'Urgente', color: 'red' }
                  ].map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setNewTaskPriority(p.value)}
                      className={`flex-1 py-3 rounded-xl font-medium text-[14px] border-2 transition-all ${
                        newTaskPriority === p.value
                          ? p.color === 'gray' 
                            ? 'bg-gray-100 border-gray-400 text-gray-700'
                            : p.color === 'orange'
                            ? 'bg-orange-50 border-[#F97316] text-[#F97316]'
                            : 'bg-red-50 border-red-500 text-red-500'
                          : isDarkMode
                            ? 'bg-slate-800 border-slate-700 text-gray-400'
                            : 'bg-white border-gray-200 text-gray-500'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Fichiers / Pièces jointes */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Fichiers
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleFileUpload(e, false)}
                  multiple
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className={`w-full p-4 rounded-xl border-2 border-dashed text-center ${
                    isDarkMode 
                      ? 'border-slate-600 text-gray-400 hover:border-slate-500' 
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  <Paperclip size={20} className="mx-auto mb-1" />
                  <span className="text-[13px]">Ajouter des fichiers</span>
                </button>
                {newTaskFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {newTaskFiles.map(file => (
                      <div key={file.id} className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <FileText size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                          <span className={`text-[12px] truncate max-w-[180px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{file.name}</span>
                          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{file.size}</span>
                        </div>
                        <button onClick={() => removeFile(file.id, false)} className="p-1">
                          <X size={14} className="text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer fixe - Se positionne au-dessus du clavier */}
            <div 
              className={`p-4 border-t transition-all duration-200 ${
                isDarkMode ? 'border-slate-700 bg-slate-900' : 'border-gray-100 bg-white'
              }`}
              style={{ 
                paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
              }}
            >
              {/* Bouton Créer avec Abracada */}
              <button
                onClick={() => {
                  const taskData = {
                    title: newTaskTitle,
                    description: newTaskDescription,
                    priority: newTaskPriority,
                    due_date: newTaskDueDate,
                    due_time: newTaskDueTime,
                    isNewTask: true
                  };
                  resetNewTaskForm();
                  setShowNewTaskModal(false);
                  setAssistantTaskContext(taskData);
                  setTimeout(() => setShowAssistant(true), 100);
                }}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all ${
                  isDarkMode 
                    ? 'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30' 
                    : 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                }`}
              >
                <Sparkles size={16} />
                Créer avec Abracada
              </button>
              
              {/* Boutons Annuler et Créer */}
              <div className="flex gap-3">
                <button
                  onClick={() => { resetNewTaskForm(); setShowNewTaskModal(false); }}
                  className={`flex-1 h-12 rounded-xl font-semibold transition-all border ${
                    isDarkMode 
                      ? 'border-slate-600 text-gray-300 hover:bg-slate-800' 
                      : 'border-gray-300 text-[#1e3a5f] hover:bg-gray-100'
                  }`}
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddTask}
                  disabled={!newTaskTitle.trim()}
                  className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50"
                >
                  Créer
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* ═══ DÉTAIL TÂCHE - PLEIN ÉCRAN via Portal ═══ */}
      {selectedTask && createPortal(
        <AnimatePresence>
          <motion.div
            key="task-detail-modal"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed inset-0 z-[10002] flex flex-col ${isDarkMode ? 'bg-[#0f172a]' : 'bg-white'}`}
          >
            {/* Header avec safe area */}
            <header 
              className={`flex items-center justify-between px-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}
              style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}
            >
              <button onClick={closeTaskDetail} className={`p-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <ArrowLeft size={24} />
              </button>
              <h1 className={`text-[17px] font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Détail tâche</h1>
              <button 
                onClick={handleUpdateTask}
                disabled={!editTitle.trim()}
                className="px-4 py-1.5 bg-[#F97316] text-white rounded-lg font-semibold text-[13px] disabled:opacity-50"
              >
                Sauver
              </button>
            </header>

            {/* Contenu */}
            <div className="flex-1 overflow-y-auto p-5">
              {/* Titre */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Titre *
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className={`w-full p-4 rounded-xl border text-[16px] ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white' 
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`}
                />
              </div>
              
              {/* Description */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Description
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={4}
                  className={`w-full p-4 rounded-xl border text-[15px] resize-none ${
                    isDarkMode 
                      ? 'bg-slate-800 border-slate-700 text-white' 
                      : 'bg-gray-50 border-gray-200 text-gray-900'
                  }`}
                />
              </div>
              
              {/* Date et Heure */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Échéance
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                      className={`w-full p-4 rounded-xl border text-[15px] ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      }`}
                    />
                  </div>
                  <div className="w-28">
                    <input
                      type="time"
                      value={editDueTime}
                      onChange={(e) => setEditDueTime(e.target.value)}
                      className={`w-full p-4 rounded-xl border text-[15px] ${
                        isDarkMode 
                          ? 'bg-slate-800 border-slate-700 text-white' 
                          : 'bg-gray-50 border-gray-200 text-gray-900'
                      }`}
                    />
                  </div>
                </div>
              </div>
              
              {/* Priorité */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Priorité
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'low', label: 'Faible', color: 'gray' },
                    { value: 'medium', label: 'Normale', color: 'orange' },
                    { value: 'high', label: 'Urgente', color: 'red' }
                  ].map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setEditPriority(p.value)}
                      className={`flex-1 py-3 rounded-xl font-medium text-[14px] border-2 transition-all ${
                        editPriority === p.value
                          ? p.color === 'gray' 
                            ? 'bg-gray-100 border-gray-400 text-gray-700'
                            : p.color === 'orange'
                            ? 'bg-orange-50 border-[#F97316] text-[#F97316]'
                            : 'bg-red-50 border-red-500 text-red-500'
                          : isDarkMode
                            ? 'bg-slate-800 border-slate-700 text-gray-400'
                            : 'bg-white border-gray-200 text-gray-500'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Fichiers */}
              <div className="mb-4">
                <label className={`block text-[12px] font-semibold mb-2 uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Fichiers
                </label>
                <input
                  type="file"
                  ref={editFileInputRef}
                  onChange={(e) => handleFileUpload(e, true)}
                  multiple
                  className="hidden"
                />
                <button
                  onClick={() => editFileInputRef.current?.click()}
                  className={`w-full p-4 rounded-xl border-2 border-dashed text-center ${
                    isDarkMode 
                      ? 'border-slate-600 text-gray-400 hover:border-slate-500' 
                      : 'border-gray-300 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  <Paperclip size={20} className="mx-auto mb-1" />
                  <span className="text-[13px]">Ajouter des fichiers</span>
                </button>
                {editFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {editFiles.map(file => (
                      <div key={file.id} className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-gray-50'}`}>
                        <div className="flex items-center gap-2">
                          <FileText size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                          <span className={`text-[12px] truncate max-w-[180px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{file.name}</span>
                          <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{file.size}</span>
                        </div>
                        <button onClick={() => removeFile(file.id, true)} className="p-1">
                          <X size={14} className="text-gray-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Supprimer */}
              <button
                onClick={handleDeleteTask}
                className="w-full py-3 mt-4 rounded-xl font-medium text-[14px] text-red-500 bg-red-50 border border-red-200 flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                Supprimer cette tâche
              </button>
            </div>
            
            {/* Bouton flottant Demander à Abra */}
            <button
              onClick={() => openAbraForTask(selectedTask)}
              className="fixed z-50 flex items-center gap-1.5 px-4 py-2.5 rounded-full text-white active:scale-95 transition-all"
              style={{ 
                bottom: '24px',
                right: '16px',
                background: 'linear-gradient(145deg, #E8955C 0%, #D4784A 100%)',
                boxShadow: '0 4px 14px rgba(232, 149, 92, 0.35)',
                border: '1px solid rgba(255, 255, 255, 0.12)'
              }}
            >
              <Sparkles size={14} className="text-white" />
              <span className="text-[12px] font-semibold">Demander à Abra</span>
            </button>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}

      {/* ═══ ASSISTANT ABRA - COMPACT & PREMIUM via Portal ═══ */}
      {showAssistant && createPortal(
        <AnimatePresence>
          <motion.div
            key="abra-assistant-modal"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className={`fixed inset-0 z-[10002] flex flex-col ${isDarkMode ? 'bg-[#0f172a]' : 'bg-[#FAF7F2]'}`}
          >
            {/* Header simple - Retour avec safe area */}
            <header 
              className={`flex items-center gap-3 px-4 border-b ${
                isDarkMode ? 'bg-[#1e293b] border-[#334155]' : 'bg-white border-gray-100'
              }`}
              style={{ paddingTop: 'max(12px, env(safe-area-inset-top))', paddingBottom: '12px' }}
            >
              <button onClick={() => setShowAssistant(false)} className="p-1">
                <ArrowLeft size={22} className={isDarkMode ? 'text-white' : 'text-[#1E3A5F]'} strokeWidth={2} />
              </button>
              <h1 className={`text-[16px] font-bold ${isDarkMode ? 'text-white' : 'text-[#1E3A5F]'}`}>Demander à Abra</h1>
            </header>

            {/* ═══ CONTENU COMPACT ═══ */}
            <div className="flex-1 overflow-y-auto px-5 pt-4 pb-4">
              {messages.length === 0 ? (
                <div>
                  {/* Bloc contexte - Affiche la tâche si contexte */}
                  {assistantTaskContext ? (
                    <div className={`p-4 rounded-2xl mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'} shadow-sm border-l-4 border-l-[#F97316]`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center flex-shrink-0">
                          {assistantTaskContext.isNewTask ? (
                            <Plus size={20} className="text-white" strokeWidth={2} />
                          ) : (
                            <Check size={20} className="text-white" strokeWidth={2} />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`text-[10px] uppercase font-semibold ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {assistantTaskContext.isNewTask ? 'Nouvelle tâche' : 'Tâche sélectionnée'}
                          </p>
                          {assistantTaskContext.title ? (
                            <h2 className={`text-[15px] font-bold mt-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{assistantTaskContext.title}</h2>
                          ) : (
                            <h2 className={`text-[15px] font-bold mt-0.5 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Créer une tâche avec Abra</h2>
                          )}
                          {assistantTaskContext.description && (
                            <p className={`text-[12px] mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{assistantTaskContext.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className={`text-[12px] ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <Sparkles size={12} className="inline mr-1 text-[#F97316]" />
                          {assistantTaskContext.isNewTask 
                            ? 'Décris-moi ce que tu dois faire, je t\'aide à créer ta tâche !'
                            : 'Comment puis-je t\'aider avec cette tâche ?'
                          }
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-3 p-4 rounded-2xl mb-4 ${isDarkMode ? 'bg-slate-800' : 'bg-white'} shadow-sm`}>
                      <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                        <Mic size={24} className="text-white" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <h2 className={`text-[17px] font-bold ${isDarkMode ? 'text-white' : 'text-[#1E3A5F]'}`}>Tes tâches</h2>
                        <p className={`text-[12px] ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Abra est connecté à ta liste de tâches</p>
                      </div>
                      <Sparkles size={18} className="text-[#F97316]" />
                    </div>
                  )}
                  
                  {/* Actions rapides - Adaptées au contexte */}
                  <p className={`text-[10px] uppercase font-semibold mb-2 px-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {assistantTaskContext ? 'Que veux-tu faire ?' : 'Actions rapides'}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {(assistantTaskContext ? [
                      { icon: Calendar, label: 'Replanifier', color: 'blue' },
                      { icon: AlertCircle, label: 'Marquer urgente', color: 'red' },
                      { icon: FileText, label: 'Résumer', color: 'orange' },
                      { icon: Zap, label: 'Automatiser', color: 'amber' }
                    ] : [
                      { icon: Plus, label: 'Créer une tâche', color: 'orange' },
                      { icon: AlertCircle, label: 'En retard', color: 'red' },
                      { icon: Calendar, label: 'Ma journée', color: 'blue' },
                      { icon: Zap, label: 'Automatiser', color: 'amber' }
                    ]).map((action, i) => (
                      <button 
                        key={i}
                        onClick={() => setInputValue(action.label)}
                        className={`flex items-center gap-3 p-3 rounded-xl text-left transition-all ${
                          isDarkMode 
                            ? 'bg-slate-800 border border-slate-700 hover:bg-slate-700' 
                            : 'bg-white border border-gray-100 hover:border-gray-200 shadow-sm'
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          action.color === 'orange' ? 'bg-orange-100' : 
                          action.color === 'red' ? 'bg-red-100' : 
                          action.color === 'blue' ? 'bg-blue-100' : 'bg-amber-100'
                        }`}>
                          <action.icon size={18} className={
                            action.color === 'orange' ? 'text-[#F97316]' : 
                            action.color === 'red' ? 'text-red-500' : 
                            action.color === 'blue' ? 'text-blue-500' : 'text-amber-500'
                          } />
                        </div>
                        <span className={`font-medium text-[13px] ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{action.label}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Aide textuelle */}
                  <p className={`text-[11px] text-center mt-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    Parle ou écris ta demande ci-dessous
                  </p>
                </div>
              ) : (
                <div className="space-y-3 py-4">
                  {messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      {m.role === 'user' ? (
                        <div className="max-w-[80%] px-4 py-2.5 rounded-2xl rounded-tr-sm bg-[#1E3A5F] text-white text-[14px]">{m.content}</div>
                      ) : (
                        <div className="max-w-[80%] flex gap-2">
                          <div className="w-8 h-8 rounded-xl bg-[#1E3A5F] flex items-center justify-center flex-shrink-0">
                            <Sparkles size={14} className="text-white" />
                          </div>
                          <div className={`px-4 py-2.5 rounded-2xl rounded-tl-sm ${isDarkMode ? 'bg-slate-800 text-gray-200' : 'bg-white text-gray-700'} text-[14px]`}>{m.content}</div>
                        </div>
                      )}
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex gap-2">
                      <div className="w-8 h-8 rounded-xl bg-[#1E3A5F] flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                      <div className={`px-4 py-2.5 rounded-2xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
                        <div className="flex gap-1">
                          <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" />
                          <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-2 h-2 bg-[#1E3A5F] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Barre de saisie - IDENTIQUE à Nouvelle demande */}
            <div className={`px-4 py-3 ${isDarkMode ? 'bg-[#1e293b]' : 'bg-white'} border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
              <div 
                className="flex items-center rounded-full"
                style={{ 
                  height: '52px',
                  paddingLeft: '16px',
                  paddingRight: '8px',
                  backgroundColor: isDarkMode ? '#2d3748' : '#ececec',
                  border: '2px solid #F97316'
                }}
              >
                <button className={`flex-shrink-0 ${isDarkMode ? 'text-gray-300' : 'text-[#676767]'}`}>
                  <Plus size={24} strokeWidth={2} />
                </button>
                
                <input
                  value={inputValue}
                  onChange={e => setInputValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && send()}
                  placeholder="Écris ta demande..."
                  style={{
                    flex: 1,
                    marginLeft: '4px',
                    marginRight: '8px',
                    fontSize: '16px',
                    fontWeight: '400',
                    color: isDarkMode ? '#fff' : '#1a1a1a',
                    caretColor: isDarkMode ? '#fff' : '#1a1a1a',
                    backgroundColor: 'transparent',
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                />
                
                <button className="flex-shrink-0" style={{ marginLeft: '8px' }}>
                  <Mic size={24} strokeWidth={1.5} className={isDarkMode ? 'text-gray-400' : 'text-[#9a9a9a]'} />
                </button>
                
                <button 
                  onClick={send} 
                  disabled={!inputValue.trim()} 
                  className="flex-shrink-0 rounded-full flex items-center justify-center bg-[#E8650A] hover:bg-[#d55a09] text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ width: '40px', height: '40px' }}
                >
                  <ArrowUp size={20} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
      
      {/* ═══ NAVBAR COCKPIT PARTICULIER - Masquée quand un modal est ouvert ═══ */}
      {!showNewTaskModal && !selectedTask && !showAssistant && !showAutoModal && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-[10001] border-t border-[#2A4A6F] md:hidden" 
          style={{ background: '#1E3A5F' }}
        >
        <div className="grid grid-cols-5 items-center justify-items-center" style={{ height: '60px' }}>
          {/* Espace */}
          <button 
            onClick={() => onNavigateToHub && onNavigateToHub('hub_espace')}
            className="flex flex-col items-center justify-center gap-[2px] w-full h-full" 
            data-testid="tasks-nav-espace"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            <span style={{ color: '#F97316', fontSize: '9px', fontWeight: 600 }}>Espace</span>
          </button>
          
          {/* Marketplace */}
          <button 
            onClick={() => onNavigateToHub && onNavigateToHub('hub_marketplace')}
            className="flex flex-col items-center justify-center gap-[2px] w-full h-full" 
            data-testid="tasks-nav-marketplace"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: 500 }}>Marketplace</span>
          </button>
          
          {/* Réseau */}
          <button 
            onClick={() => onNavigateToHub && onNavigateToHub('hub_reseau')}
            className="flex flex-col items-center justify-center gap-[2px] w-full h-full" 
            data-testid="tasks-nav-reseau"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: 500 }}>Réseau</span>
          </button>
          
          {/* Service */}
          <button 
            onClick={() => onNavigateToHub && onNavigateToHub('hub_prestataires')}
            className="flex flex-col items-center justify-center gap-[2px] w-full h-full" 
            data-testid="tasks-nav-service"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: 500 }}>Service</span>
          </button>
          
          {/* Emploi */}
          <button 
            onClick={() => onNavigateToHub && onNavigateToHub('hub_emploi')}
            className="flex flex-col items-center justify-center gap-[2px] w-full h-full" 
            data-testid="tasks-nav-emploi"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
            </svg>
            <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '9px', fontWeight: 500 }}>Emploi</span>
          </button>
        </div>
      </div>
      )}
    </div>
  );
}
